import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { getDeviceFingerprint } from "@/lib/device";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { termsExpired } from "@/lib/time";
import { Mail, Lock, KeyRound, ShieldCheck, RefreshCw, MapPin } from "lucide-react";

const HERO_DESKTOP = "https://customer-assets-m6fa6gv7.emergentagent.net/job_confidential-ops-hub/artifacts/pwf1ym6l_B65AB6CD-8AFC-4CFF-A1A8-AB88E0980473.png";
const HERO_MOBILE = "https://customer-assets-m6fa6gv7.emergentagent.net/job_confidential-ops-hub/artifacts/aokv6ny0_5B3A9044-44AD-4901-9C85-AF461189A0DA.png";
const RESEND_COOLDOWN = 30;

export default function Login() {
  const [mode, setMode] = useState("otp");
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [geo, setGeo] = useState(null);
  const [geoErr, setGeoErr] = useState("");
  const cooldownRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => () => { if (cooldownRef.current) clearInterval(cooldownRef.current); }, []);

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(cooldownRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const askGeo = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) { setGeoErr("Geolocation not supported by this browser."); return reject(new Error("no-geo")); }
    navigator.geolocation.getCurrentPosition(
      (p) => { const g = { lat: p.coords.latitude, lng: p.coords.longitude }; setGeo(g); setGeoErr(""); resolve(g); },
      () => { const msg = "Location access is required to sign in. Please allow it and retry."; setGeoErr(msg); reject(new Error(msg)); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  });

  const goHome = (user) => {
    // Master admin never sees T&C. Franchise users re-sign every 15 days.
    if (user.role === "admin") { window.location.href = "/admin"; return; }
    if (termsExpired()) window.location.href = "/terms";
    else window.location.href = "/viewer";
  };

  const requestOtp = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      try { await askGeo(); }
      catch { toast.error("Please allow location access — sign-in is location-restricted."); setLoading(false); return; }
      await api.post("/auth/request-otp", { email });
      toast.success("OTP sent to your email");
      setStep("code"); startCooldown();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send OTP. Please retry shortly.");
    } finally { setLoading(false); }
  };

  const resendOtp = async () => {
    if (cooldown > 0) return;
    setResending(true);
    try {
      await api.post("/auth/request-otp", { email });
      toast.success("A new OTP has been sent");
      startCooldown();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend OTP. Try again shortly.");
    } finally { setResending(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", {
        email, code,
        device_fingerprint: getDeviceFingerprint(),
        lat: geo?.lat, lng: geo?.lng
      });
      setToken(data.token); toast.success("Welcome"); goHome(data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally { setLoading(false); }
  };

  const passwordLogin = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/login-password", { email, password });
      setToken(data.token); toast.success("Welcome, Admin"); goHome(data.user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally { setLoading(false); }
  };

  const forgot = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If this account exists, a reset link has been sent to your email.");
      setMode("password");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reset link");
    } finally { setLoading(false); }
  };

  const TabBtn = ({ id, label }) => (
    <button data-testid={`tab-${id}`} onClick={() => { setMode(id); setStep("email"); }}
      className={`flex-1 py-2.5 rounded-full text-[11px] font-mono2 uppercase tracking-[0.25em] transition-all ${
        mode === id ? "text-[#F5C242] shadow-[0_4px_14px_-4px_rgba(59,37,25,0.5)]" : "text-[#7a6349] hover:text-[#3B2519]"
      }`}
      style={mode === id ? { background: "linear-gradient(135deg,#3B2519,#5a3826)" } : {}}>
      {label}
    </button>
  );

  return (
    <div className="min-h-screen relative"
      style={{ background: "linear-gradient(180deg, #FFFBEE 0%, #FBF4DA 60%, #F5EBCB 100%)" }}>
      <div className="relative z-10">
        {/* Full-screen branded illustration hero — separate art for mobile (tall) and desktop (wide). Uses `contain` so no crop. */}
        <section className="relative w-full flex items-center justify-center" style={{ minHeight: "100vh", background: "#FFFBEE" }}>
          <img src={HERO_MOBILE} alt="Chai Chaska Cafe · SOP Portal" draggable={false}
            fetchpriority="high" loading="eager"
            className="md:hidden max-w-full max-h-[92vh] w-auto h-auto select-none block"
            style={{ objectFit: "contain" }} />
          <img src={HERO_DESKTOP} alt="Chai Chaska Cafe · SOP Portal" draggable={false}
            fetchpriority="high" loading="eager"
            className="hidden md:block max-w-full max-h-[92vh] w-auto h-auto select-none"
            style={{ objectFit: "contain" }} />
          <div className="absolute inset-x-0 bottom-4 flex justify-center pointer-events-none">
            <button type="button" onClick={() => {
                const el = document.getElementById("cc-signin");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              data-testid="scroll-to-signin"
              className="pointer-events-auto inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-mono2 uppercase tracking-[0.3em] shadow-lg animate-bounce"
              style={{ background: "#3B2519", color: "#F5C242" }}>
              Scroll to sign in ↓
            </button>
          </div>
        </section>

        {/* Sign-in card */}
        <section id="cc-signin" className="w-full flex items-center justify-center px-4 py-10 md:py-14">
          <div className="w-full max-w-md rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl backdrop-blur-md"
            style={{ background: "rgba(255,253,246,0.98)", border: "1px solid #ECD9A5" }}>
            <div className="font-mono2 text-[10px] tracking-[0.4em] uppercase mb-2" style={{ color: "#B4841F" }}>
              Secure Sign-in
            </div>
            <h2 className="font-display text-2xl sm:text-3xl mb-6" style={{ color: "#2b1a0d" }}>
              {mode === "forgot" ? "Reset your password" : mode === "password" ? "Sign in with password" : (step === "email" ? "Sign in with registered email id" : "Enter access code")}
            </h2>

            {mode !== "forgot" && (
              <div className="flex gap-1 mb-6 p-1 rounded-full border" style={{ background: "#F8F1DE", borderColor: "#ECD9A5" }}>
                <TabBtn id="otp" label="Email OTP" />
                <TabBtn id="password" label="Admin Password" />
              </div>
            )}

            {mode === "otp" && step === "email" && (
              <form onSubmit={requestOtp} className="space-y-4">
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#B4841F" }} />
                  <Input data-testid="login-email-input" type="email" required autoFocus
                    placeholder="Registered email id" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 bg-white text-base" style={{ borderColor: "#ECD9A5", color: "#2b1a0d" }} />
                </div>
                <Button data-testid="send-otp-btn" disabled={loading} className="w-full h-12 rounded-full font-bold text-base btn-lift"
                  style={{ background: "#F5C242", color: "#1A1200" }}>
                  {loading ? "Sending..." : "Send OTP"}
                </Button>
                <p className="text-[10px] font-mono2 uppercase tracking-[0.3em] text-center pt-2" style={{ color: "#a08862" }}>
                  Authorised access only · location-verified sign-in
                </p>
              </form>
            )}

            {mode === "otp" && step === "code" && (
              <form onSubmit={verifyOtp} className="space-y-4">
                <p className="text-sm mb-2 font-medium" style={{ color: "#7a6349" }}>Sent to <b>{email}</b></p>
                <Input data-testid="otp-code-input" required autoFocus inputMode="numeric" maxLength={6}
                  placeholder="6-digit code" value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="h-14 text-center text-2xl font-mono2 tracking-[0.8em] bg-white"
                  style={{ borderColor: "#ECD9A5", color: "#2b1a0d" }} />
                <Button data-testid="verify-otp-btn" disabled={loading || code.length < 6} className="w-full h-12 rounded-full font-bold text-base btn-lift"
                  style={{ background: "#F5C242", color: "#1A1200" }}>
                  {loading ? "Verifying..." : "Verify & Enter"}
                </Button>

                <div className="flex items-center justify-between pt-1">
                  <button type="button" onClick={() => setStep("email")}
                    data-testid="change-email-btn"
                    className="text-[10px] font-mono2 uppercase tracking-[0.25em]" style={{ color: "#a08862" }}>
                    ← Change email
                  </button>
                  <button type="button" onClick={resendOtp} disabled={cooldown > 0 || resending}
                    data-testid="resend-otp-btn"
                    className="text-[10px] font-mono2 uppercase tracking-[0.25em] inline-flex items-center gap-1 disabled:opacity-40"
                    style={{ color: "#B4841F" }}>
                    <RefreshCw size={11} className={resending ? "animate-spin" : ""} />
                    {cooldown > 0 ? `Resend in ${cooldown}s` : (resending ? "Sending..." : "Resend OTP")}
                  </button>
                </div>

                {geo && <p className="text-[10px] font-mono2 uppercase tracking-[0.25em] text-center pt-1" style={{ color: "#8a6e40" }}>Location captured · geofence enforced</p>}
                {geoErr && <p className="text-[10px] font-mono2 uppercase tracking-[0.25em] text-center pt-1" style={{ color: "#c0392b" }}>Location denied — sign-in may fail if you are outside your outlet.</p>}
              </form>
            )}

            {mode === "password" && (
              <form onSubmit={passwordLogin} className="space-y-4">
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#B4841F" }} />
                  <Input data-testid="pw-email-input" type="email" required autoFocus
                    placeholder="admin@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 bg-white" style={{ borderColor: "#ECD9A5", color: "#2b1a0d" }} />
                </div>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#B4841F" }} />
                  <Input data-testid="pw-password-input" type="password" required minLength={6}
                    placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-12 bg-white" style={{ borderColor: "#ECD9A5", color: "#2b1a0d" }} />
                </div>
                <Button data-testid="pw-submit-btn" disabled={loading} className="w-full h-12 rounded-full font-bold text-base btn-lift"
                  style={{ background: "#F5C242", color: "#1A1200" }}>
                  {loading ? "Signing in..." : "Sign in"}
                </Button>
                <div className="flex items-center justify-between text-[10px] font-mono2 uppercase tracking-[0.25em] pt-2">
                  <button type="button" data-testid="forgot-link" onClick={() => setMode("forgot")} style={{ color: "#B4841F" }}>
                    Forgot password?
                  </button>
                  <span style={{ color: "#a08862" }}>Admin only</span>
                </div>
              </form>
            )}

            {mode === "forgot" && (
              <form onSubmit={forgot} className="space-y-4">
                <p className="text-sm font-medium" style={{ color: "#7a6349" }}>
                  Enter your admin email. We&apos;ll send a reset link if the account exists.
                </p>
                <div className="relative">
                  <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#B4841F" }} />
                  <Input data-testid="forgot-email-input" type="email" required autoFocus
                    placeholder="admin@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 bg-white" style={{ borderColor: "#ECD9A5", color: "#2b1a0d" }} />
                </div>
                <Button data-testid="forgot-submit-btn" disabled={loading} className="w-full h-12 rounded-full font-bold text-base btn-lift"
                  style={{ background: "#F5C242", color: "#1A1200" }}>
                  {loading ? "Sending..." : "Send reset link"}
                </Button>
                <button type="button" data-testid="back-to-signin" onClick={() => setMode("password")}
                  className="w-full text-[10px] font-mono2 uppercase tracking-[0.25em] pt-2" style={{ color: "#a08862" }}>
                  ← Back to sign in
                </button>
              </form>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
